import { useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const REALTIME_URL = `${API_BASE.replace(/^http/, 'ws').replace(/\/$/, '')}/realtime/places`;

export default function usePlaceLoadRealtime({ placeIds, onUpdate, onRefresh }) {
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const placeIdsRef = useRef([]);
  const onUpdateRef = useRef(onUpdate);
  const onRefreshRef = useRef(onRefresh);

  onUpdateRef.current = onUpdate;
  onRefreshRef.current = onRefresh;
  placeIdsRef.current = [...new Set(placeIds.filter(Boolean).map(String))];
  const placeIdsKey = placeIdsRef.current.join('|');

  const sendSubscription = () => {
    const socket = socketRef.current;
    if (socket?.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({
      type: 'SUBSCRIBE_PLACES',
      placeIds: placeIdsRef.current,
    }));
  };

  useEffect(() => {
    let active = true;

    const connect = () => {
      if (!active) return;
      const socket = new WebSocket(REALTIME_URL);
      socketRef.current = socket;

      socket.onopen = () => {
        reconnectAttemptRef.current = 0;
        sendSubscription();
        onRefreshRef.current?.();
      };
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'PLACE_LOAD_CHANGED') onUpdateRef.current?.(message);
        } catch (_) {}
      };
      socket.onclose = () => {
        if (!active) return;
        const delay = Math.min(1000 * (2 ** reconnectAttemptRef.current), 30000);
        reconnectAttemptRef.current += 1;
        reconnectTimerRef.current = setTimeout(connect, delay);
      };
      socket.onerror = () => socket.close();
    };

    connect();
    const refreshInterval = setInterval(() => onRefreshRef.current?.(), 90000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') onRefreshRef.current?.();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      active = false;
      clearTimeout(reconnectTimerRef.current);
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
      socketRef.current?.close();
    };
  }, []);

  useEffect(() => {
    const socket = socketRef.current;
    if (socket?.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({
      type: 'SUBSCRIBE_PLACES',
      placeIds: placeIdsRef.current,
    }));
  }, [placeIdsKey]);
}
